// New-order alerts (user 2026-10-15): a sound for every new order — Jumia, Marjane, Shopify, each its own sound —
// a card in the corner, and a system notification when the app is in the background. Everyone hears it; each
// person turns it on/off on their own device with the 🔔 button (saved on that device). The app asks the server
// every 30 s (routes/notify.js). Phones keep sound muted until the page is touched once — the bell shows "tap".
import { FC, useEffect, useRef, useState } from 'react';
import { playCash } from './cashSound';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { urls } from '../../dispatch/urls';

type Platform = 'jumia' | 'marjan' | 'shopify';
interface NewOrder { platform: Platform; store: string; product: string; price: number | null; at: string }
// ⚠️ a good Jumia SKU that stopped being live (admins only — backend controllers/listing-alerts.js)
interface ListingAlert { id: string; sku: string; store: string; product: string; label: string; orders60: number; wasLead: boolean; newLeadSku: string | null }

const KEY = 'orderSound';
const readOn = () => { try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; } };
const LABEL: Record<Platform, string> = { jumia: 'Jumia', marjan: 'Marjane', shopify: 'Shopify' };
const COLOR: Record<Platform, string> = { jumia: '#f68b1e', marjan: '#e2001a', shopify: '#5e8e3e' };

let ctx: AudioContext | null = null;
const audio = () => { if (!ctx) { const A = (window as any).AudioContext || (window as any).webkitAudioContext; if (A) ctx = new A(); } return ctx; };
const tone = (c: AudioContext, freq: number, start: number, dur: number, type: OscillatorType = 'sine', vol = 0.25) => {
  const o = c.createOscillator(), g = c.createGain();
  o.type = type; o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, start); g.gain.exponentialRampToValueAtTime(vol, start + 0.015); g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(c.destination); o.start(start); o.stop(start + dur + 0.05);
};
// every new order rings the cash register (user 2026-10-25), whatever the platform
const play = (_p: Platform) => playCash(audio());

const OrderAlerts: FC<{ token: string }> = ({ token }) => {
  const [on, setOn] = useState(readOn);
  const [locked, setLocked] = useState(true);   // phone/browser audio not unlocked yet
  const [cards, setCards] = useState<{ id: number; orders: NewOrder[] }[]>([]);
  const [skuAlerts, setSkuAlerts] = useState<ListingAlert[]>([]);   // stay until clicked away
  const since = useRef<string | null>(null);
  const onRef = useRef(on); onRef.current = on;

  // unlock audio on the first touch anywhere (browser rule)
  useEffect(() => {
    const unlock = () => { const c = audio(); c?.resume().then(() => setLocked(c.state !== 'running')).catch(() => {}); };
    window.addEventListener('pointerdown', unlock, { once: false });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (!token) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${urls.baseUrlRestApi}/notify/orders`, { params: { since: since.current || undefined }, headers: { 'x-auth-token': token }, timeout: 20000 });
        if (stop) return;
        const first = since.current == null;
        since.current = r.data?.now || new Date().toISOString();
        const fresh: ListingAlert[] = first ? [] : (r.data?.listingAlerts || []);
        if (fresh.length) {
          setSkuAlerts((as) => [...as, ...fresh.filter((a) => !as.some((x) => x.id === a.id))].slice(-5));
          if (onRef.current) { const c = audio(); if (c && c.state === 'running') { const t = c.currentTime + 0.02; tone(c, 440, t, 0.25, 'square', 0.12); tone(c, 330, t + 0.28, 0.4, 'square', 0.12); } }
          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') new Notification(`⚠️ ${fresh[0].label}: ${fresh[0].sku}`, { body: `${fresh[0].store} · ${fresh[0].product}` });
        }
        const orders: NewOrder[] = first ? [] : (r.data?.orders || []);   // the first call only sets the starting point
        if (!orders.length || !onRef.current) return;
        // one sound per platform present (Shopify first: the store is ours), a short gap between them
        const plats = (['shopify', 'jumia', 'marjan'] as Platform[]).filter((p) => orders.some((o) => o.platform === p));
        plats.forEach((p, i) => setTimeout(() => play(p), i * 900));
        const id = Date.now();
        setCards((cs) => [...cs.slice(-2), { id, orders }]);
        setTimeout(() => setCards((cs) => cs.filter((c) => c.id !== id)), 10000);
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
          const o = orders[0];
          new Notification(orders.length > 1 ? `🛒 ${orders.length} new orders` : `🛒 New ${LABEL[o.platform]} order`, { body: orders.length > 1 ? orders.map((x) => `${LABEL[x.platform]}: ${x.product}`).slice(0, 4).join('\n') : `${o.product}${o.price ? ` · ${Math.round(o.price)} MAD` : ''}${o.store ? ` · ${o.store}` : ''}`, icon: '/logo192.png', tag: 'new-order' });
        }
      } catch { /* offline / server restarting: next tick */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [token]);

  const toggle = () => {
    const next = !on; setOn(next);
    try { localStorage.setItem(KEY, next ? 'on' : 'off'); } catch { /* private mode */ }
    const c = audio(); c?.resume().then(() => { setLocked(c.state !== 'running'); if (next) play('jumia'); }).catch(() => {});
    if (next && 'Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
  };

  // the bell sits in the top bar (next to the theme button); a floating one only where there is no top bar
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { const find = () => setSlot(document.getElementById('order-bell-slot')); find(); const t = setInterval(find, 2000); return () => clearInterval(t); }, []);
  const bell = (
    <button onClick={toggle} title={on ? 'New-order sound ON — tap to turn off' : 'New-order sound OFF — tap to turn on'} aria-label="New-order sound"
      style={slot ? { position: 'relative', width: 34, height: 34, borderRadius: 17, border: 'none', background: 'transparent', fontSize: 17, cursor: 'pointer' }
        : { position: 'fixed', left: 12, bottom: 12, zIndex: 90, width: 40, height: 40, borderRadius: 20, border: '1px solid #e2e8f0', background: on ? '#fff7ed' : '#f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,.12)', fontSize: 18, cursor: 'pointer' }}>
      {on ? '🔔' : '🔕'}
      {on && locked && <span style={{ position: 'absolute', top: -4, right: -10, background: '#f68b1e', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 8, padding: '1px 5px' }}>tap</span>}
    </button>
  );
  return (
    <>
      {slot ? createPortal(bell, slot) : bell}
      <div style={{ position: 'fixed', right: 12, bottom: 12, zIndex: 95, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'calc(100vw - 80px)' }}>
        {skuAlerts.map((a) => (
          <div key={a.id} onClick={() => setSkuAlerts((as) => as.filter((x) => x.id !== a.id))} title="Tap to close — the open alerts stay in Pricing Intelligence → Auto-Pilot → Listing issues"
            style={{ background: '#fff', borderRadius: 12, boxShadow: '0 6px 20px rgba(0,0,0,.18)', padding: '10px 14px', borderLeft: '5px solid #dc2626', minWidth: 240, cursor: 'pointer' }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#b91c1c' }}>⚠️ {a.label} — {a.store}</div>
            <div style={{ fontSize: 12, color: '#334155', marginTop: 3 }}>{a.product.slice(0, 50)}</div>
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>SKU {a.sku} · {a.orders60} order(s) in 60 days{a.wasLead ? ' · was the LEAD' : ''}{a.newLeadSku ? ` → new lead ${a.newLeadSku}` : ''}</div>
            <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 2, fontWeight: 700 }}>Fix it in Vendor Center → Products</div>
          </div>
        ))}
        {cards.map((c) => (
          <div key={c.id} onClick={() => setCards((cs) => cs.filter((x) => x.id !== c.id))}
            style={{ background: '#fff', borderRadius: 12, boxShadow: '0 6px 20px rgba(0,0,0,.18)', padding: '10px 14px', borderLeft: `5px solid ${COLOR[c.orders[0].platform]}`, minWidth: 240, cursor: 'pointer' }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>🛒 {c.orders.length > 1 ? `${c.orders.length} new orders` : `New ${LABEL[c.orders[0].platform]} order`}</div>
            {c.orders.slice(0, 4).map((o, i) => (
              <div key={i} style={{ fontSize: 12, color: '#334155', marginTop: 3 }}>
                <b style={{ color: COLOR[o.platform] }}>{LABEL[o.platform]}</b> · {o.product.slice(0, 48)}{o.price ? ` · ${Math.round(o.price)} MAD` : ''}{o.store ? <span style={{ color: '#94a3b8' }}> · {o.store}</span> : null}
              </div>
            ))}
            {c.orders.length > 4 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>+{c.orders.length - 4} more</div>}
          </div>
        ))}
      </div>
    </>
  );
};

export default OrderAlerts;
